import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Phone, MapPin, ShieldCheck, Banknote } from "lucide-react";
import { toast } from "sonner";
import { http, formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import PriceDetails from "@/components/PriceDetails";
import { AddressForm, validateAddress, loadSaved, rememberCustomer } from "@/components/AddressForm";

const Step = ({ n, title, active, done, children }) => (
  <div className="bg-white rounded-sm fk-shadow mb-3" data-testid={`cod-step-${n}`}>
    <div className={`flex items-center gap-3 px-4 py-3 ${active ? "bg-[#C62828] text-white" : ""}`}>
      <span className={`w-6 h-6 rounded-sm flex items-center justify-center text-xs font-bold ${active ? "bg-white text-[#C62828]" : "bg-[#f0f0f0] text-[#878787]"}`}>
        {done ? <CheckCircle2 className="w-4 h-4 text-[#388e3c]" /> : n}
      </span>
      <span className={`font-head font-semibold ${active ? "text-white" : "text-[#878787]"}`}>{title}</span>
    </div>
    {children}
  </div>
);

const ItemPicker = ({ items, selected, toggle }) => (
  <div className="bg-white rounded-sm fk-shadow mb-3" data-testid="cod-items">
    <p className="px-4 py-3 border-b border-[#f0f0f0] font-head font-semibold text-[#212121]">
      Items in this COD order <span className="text-xs text-[#878787] font-normal">(untick to leave in cart)</span>
    </p>
    {items.map((i) => (
      <label key={i.id} className="flex items-center gap-3 px-4 py-3 border-b border-[#f7f7f7] cursor-pointer" data-testid={`cod-item-${i.id}`}>
        <input
          type="checkbox"
          checked={selected.includes(i.id)}
          onChange={() => toggle(i.id)}
          className="w-4 h-4 accent-[#C62828]"
          data-testid={`cod-item-check-${i.id}`}
        />
        <img src={i.image_url} alt="" className="w-12 h-12 object-contain" />
        <span className="flex-1 text-sm text-[#212121]">{i.name} <span className="text-[#878787]">× {i.qty}</span></span>
        <span className="text-sm font-semibold">{formatPrice(i.price * i.qty)}</span>
      </label>
    ))}
  </div>
);

const CodCheckout = () => {
  const navigate = useNavigate();
  const { items, removeItem } = useCart();
  const [selected, setSelected] = useState(() => items.map((i) => i.id));
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState(() => loadSaved().phone);
  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [addr, setAddr] = useState(() => loadSaved().addr);
  const [pinStatus, setPinStatus] = useState(null);

  const chosen = items.filter((i) => selected.includes(i.id));
  const totalMrp = chosen.reduce((s, i) => s + i.mrp * i.qty, 0);
  const totalPrice = chosen.reduce((s, i) => s + i.price * i.qty, 0);
  const totalQty = chosen.reduce((s, i) => s + i.qty, 0);
  const codFee = Math.round(totalPrice * 0.12);
  const codTotal = totalPrice + codFee;

  if (items.length === 0) {
    return (
      <main className="max-w-[700px] mx-auto px-4 py-16 text-center text-[#878787]">
        Your cart is empty. <a href="/" className="text-[#C62828] font-semibold">Go shopping</a>
      </main>
    );
  }

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const sendOtp = async () => {
    if (phone.length !== 10) return toast.error("Enter a valid 10-digit mobile number");
    setSending(true);
    try {
      await http.post("/checkout/cod/send-otp", {
        phone,
        items: chosen.map((i) => ({ id: i.id, name: i.name, price: i.price, mrp: i.mrp, qty: i.qty, image_url: i.image_url })),
      });
      rememberCustomer({ phone });
      toast.success("OTP sent to +91 " + phone);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send OTP");
    } finally {
      setSending(false);
    }
  };

  const verifyOtp = async () => {
    setVerifying(true);
    try {
      await http.post("/checkout/cod/verify-otp", { phone, otp });
      setAddr((a) => ({ ...a, phone: a.phone || phone }));
      toast.success("Mobile number verified");
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Incorrect OTP");
    } finally {
      setVerifying(false);
    }
  };

  const placeOrder = async () => {
    if (chosen.length === 0) return toast.error("Tick at least one item");
    const err = validateAddress(addr, pinStatus);
    if (err) return toast.error(err);
    setPlacing(true);
    rememberCustomer({ addr });
    try {
      http.post("/contacts/capture", { phone, name: addr.name, address: addr }).catch(() => {});
      const { data } = await http.post("/orders/cod", {
        phone,
        items: chosen.map((i) => ({ id: i.id, name: i.name, price: i.price, mrp: i.mrp, qty: i.qty, image_url: i.image_url })),
        address: addr,
      });
      chosen.forEach((i) => removeItem(i.id, false));
      navigate(`/order/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "COD order failed");
      setPlacing(false);
    }
  };

  return (
    <main className="max-w-[1400px] mx-auto px-2 sm:px-4 py-3" data-testid="cod-checkout-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        <div className="lg:col-span-8">
          <div className="flex items-center gap-2 bg-[#fff8e1] border border-[#ffe0b2] text-[#e65100] text-sm font-semibold px-4 py-2 rounded-sm mb-3">
            <Banknote className="w-4 h-4" /> Cash on Delivery · +12% COD fee · Pay when it arrives
          </div>
          <ItemPicker items={items} selected={selected} toggle={toggle} />

          <Step n={1} title="Mobile Number" active={step === 1} done={step > 1}>
            {step === 1 && (
              <div className="p-4 flex gap-2 max-w-md">
                <div className="flex items-center gap-2 border border-[#c2c2c2] rounded-sm px-3 h-10 text-sm text-[#555] bg-[#f7f7f7]">
                  <Phone className="w-4 h-4" /> +91
                </div>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="10-digit mobile number"
                  inputMode="numeric"
                  className="flex-1 h-10 border border-[#c2c2c2] rounded-sm px-3 text-sm outline-none focus:border-[#C62828]"
                  data-testid="cod-phone-input"
                />
                <button onClick={sendOtp} disabled={sending} className="bg-[#fb641b] text-white font-semibold px-5 rounded-sm whitespace-nowrap disabled:opacity-60" data-testid="cod-send-otp-btn">
                  {sending ? "SENDING…" : "SEND OTP"}
                </button>
              </div>
            )}
            {step > 1 && (
              <p className="px-4 py-2 text-sm text-[#555]" data-testid="cod-phone-summary">
                <CheckCircle2 className="w-4 h-4 text-[#388e3c] inline mr-1" /> +91 {phone}
              </p>
            )}
          </Step>

          <Step n={2} title="Verify OTP" active={step === 2} done={step > 2}>
            {step === 2 && (
              <div className="p-4 space-y-2">
                <p className="text-sm text-[#555]">Enter the OTP sent by SMS to +91 {phone}</p>
                <div className="flex gap-2 max-w-md">
                  <input
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="Enter SMS OTP"
                    inputMode="numeric"
                    className="flex-1 h-10 border border-[#c2c2c2] rounded-sm px-3 text-sm outline-none focus:border-[#C62828] tracking-widest"
                    data-testid="cod-otp-input"
                  />
                  <button onClick={verifyOtp} disabled={verifying || otp.length < 4} className="bg-[#C62828] text-white font-semibold px-5 rounded-sm disabled:opacity-60" data-testid="cod-verify-otp-btn">
                    {verifying ? "VERIFYING…" : "VERIFY"}
                  </button>
                </div>
                <div className="flex gap-4 text-sm font-semibold text-[#C62828]">
                  <button onClick={sendOtp} disabled={sending} className="disabled:opacity-50" data-testid="cod-resend-otp-btn">{sending ? "Sending…" : "RESEND OTP"}</button>
                  <button onClick={() => { setStep(1); setOtp(""); }} data-testid="cod-change-phone-btn">CHANGE NUMBER</button>
                </div>
              </div>
            )}
            {step > 2 && (
              <p className="px-4 py-2 text-sm text-[#388e3c]"><ShieldCheck className="w-4 h-4 inline mr-1" /> Verified</p>
            )}
          </Step>

          <Step n={3} title="Delivery Address" active={step === 3}>
            {step === 3 && (
              <AddressForm addr={addr} setAddr={setAddr} pinStatus={pinStatus} setPinStatus={setPinStatus}>
                <button
                  onClick={placeOrder}
                  disabled={placing || chosen.length === 0}
                  className="bg-[#fb641b] text-white font-semibold px-8 h-12 rounded-sm mt-1 disabled:opacity-60 flex items-center gap-2"
                  data-testid="cod-place-order-btn"
                >
                  <MapPin className="w-5 h-5" />
                  {placing ? "Placing Order…" : `PLACE COD ORDER · ${formatPrice(codTotal)}`}
                </button>
              </AddressForm>
            )}
          </Step>
        </div>

        <div className="lg:col-span-4 lg:sticky lg:top-32">
          <PriceDetails totalMrp={totalMrp} discount={totalMrp - totalPrice} delivery={0} payable={totalPrice} totalQty={totalQty} codFee={codFee} />
        </div>
      </div>
    </main>
  );
};

export default CodCheckout;
